import {
  editPropertySchema,
  PropertyFormFields,
  PropertyPriceFormFields,
  propertyPriceSchema,
  ResaleListingFormFields,
} from "@/modules/property-tracks/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PaymentTypes } from "@/modules/property-tracks/dtos";
import {
  createSchemaWithMandates,
  enforceMandates,
} from "@/modules/common/utils/zod";
import {
  ListingDescriptionFormFields,
  listingDescriptionSchema,
  ResaleDocumentsFormFields,
  resaleDocumentsSchema,
} from "@/modules/property-tracks/utils/listing";
import { useMultiFormValidation } from "@/modules/common/hooks/multi-form-validation.hook";
import { resaleListingMandatedFields } from "@/modules/property-tracks/constants/mandations";

export default function useResaleListingForms({
  defaultValues,
}: {
  defaultValues: ResaleListingFormFields;
}) {
  const propertyDetailsForm = useForm<PropertyFormFields>({
    resolver: zodResolver(editPropertySchema),
    defaultValues: defaultValues.propertyDetails,
  });

  const propertyPriceForm = useForm<PropertyPriceFormFields>({
    resolver: zodResolver(
      propertyPriceSchema.superRefine((data, ctx) => {
        switch (data?.paymentType) {
          case PaymentTypes.CASH:
            enforceMandates(data, { price: true }, ctx, []);
            break;
          case PaymentTypes.INSTALLMENTS:
            enforceMandates(
              data,
              {
                downPayment: true,
                remainingInstallments: true,
                durationRemainingMonths: true,
                durationRemainingYears: true,
                installmentFrequency: true,
              },
              ctx,
              []
            );

            break;
          default:
            enforceMandates(data, { paymentType: true }, ctx, []);
            break;
        }
      })
    ),
    defaultValues: defaultValues.propertyPriceDetails,
  });

  const resaleDocumentsForm = useForm<ResaleDocumentsFormFields>({
    resolver: zodResolver(resaleDocumentsSchema),
    defaultValues: defaultValues.resaleDocumentsDetails,
  });

  const listingDescriptionForm = useForm<ListingDescriptionFormFields>({
    resolver: zodResolver(listingDescriptionSchema),
    defaultValues: defaultValues.listingDescriptionDetails,
  });

  // Setup validation hook with base schemas
  const baseValidation = useMultiFormValidation([
    {
      form: propertyDetailsForm,
      schema: editPropertySchema,
      stepIndex: 0,
    },
    {
      form: propertyPriceForm,
      schema: propertyPriceSchema,
      stepIndex: 1,
    },
    {
      form: resaleDocumentsForm,
      schema: resaleDocumentsSchema,
      stepIndex: 2,
    },
    {
      form: listingDescriptionForm,
      schema: listingDescriptionSchema,
      stepIndex: 3,
    },
  ]);

  // Setup validation hook with mandated schemas
  const publishValidation = useMultiFormValidation([
    {
      form: propertyDetailsForm,
      schema: createSchemaWithMandates(
        editPropertySchema,
        resaleListingMandatedFields.propertyDetailsMandates
      ),
      stepIndex: 0,
    },
    {
      form: propertyPriceForm,
      schema: createSchemaWithMandates(
        propertyPriceSchema,
        resaleListingMandatedFields.propertyPriceDetailsMandates
      ),
      stepIndex: 1,
    },
    {
      form: resaleDocumentsForm,
      schema: createSchemaWithMandates(
        resaleDocumentsSchema,
        resaleListingMandatedFields.resaleDocumentsDetailsMandates
      ),
      stepIndex: 2,
    },
    {
      form: listingDescriptionForm,
      schema: createSchemaWithMandates(
        listingDescriptionSchema,
        resaleListingMandatedFields.listingDescriptionDetailsMandates
      ),
      stepIndex: 3,
    },
  ]);

  const validate = async (publish: boolean) => {
    const validation = publish ? publishValidation : baseValidation;
    return await validation.validateForms();
  };

  const reset = (defaultValues?: ResaleListingFormFields) => {
    propertyDetailsForm.reset(defaultValues?.propertyDetails);
    propertyPriceForm.reset(defaultValues?.propertyPriceDetails);
    resaleDocumentsForm.reset(defaultValues?.resaleDocumentsDetails);
    listingDescriptionForm.reset(defaultValues?.listingDescriptionDetails);
  };

  return {
    propertyDetailsForm,
    propertyPriceForm,
    resaleDocumentsForm,
    listingDescriptionForm,

    validate,
    reset
  };
}
